import { useState } from "react";
import { C, font } from "../styles/tokens";
import { useDense } from "./FormDensity";

/**
 * LineField is the standard form input for the entire app.
 *
 * Visual: bold label above (12px), thin underline (1.5px) that turns blue on
 * focus or when filled. Tight vertical spacing - 12px between fields. Looks
 * like LinkedIn's compact form pattern.
 *
 * Supported props:
 *   label, placeholder, type, value, onChange, hint, options (for select),
 *   as ("input" | "textarea" | "select"), required, disabled, autoComplete
 */
export function LineField({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  hint,
  options,
  as = "input",
  required,
  disabled,
  autoComplete,
  style,
  ...rest
}) {
  const [focused, setFocused] = useState(false);
  const dense = useDense();
  const hasValue = value !== "" && value !== null && value !== undefined;

  const inputStyle = {
    width: "100%",
    padding:
      as === "textarea"
        ? dense
          ? "3px 0"
          : "6px 0"
        : dense
          ? "3px 0 2px"
          : "7px 0 6px",
    fontSize: style?.fontSize,
    fontFamily: font,
    fontWeight: 500,
    border: 0,
    borderBottom: focused
      ? `2px solid ${C.blue}`
      : `1.5px solid ${hasValue ? "rgba(8,131,62,0.35)" : C.dividerStrong}`,
    background: "transparent",
    color: disabled ? C.textLight : C.textDark,
    outline: "none",
    boxSizing: "border-box",
    borderRadius: 0,
    transition: "border-color 0.18s ease, border-width 0.18s ease",
    ...(as === "textarea"
      ? { minHeight: dense ? 30 : 56, resize: "vertical" }
      : {}),
    ...style,
  };

  const labelStyle = {
    display: "block",
    fontSize: dense ? 11 : 12,
    fontWeight: 600,
    color: focused ? C.blue : C.textDark,
    marginBottom: dense ? 1 : 2,
    transition: "color 0.15s ease",
    letterSpacing: 0.1,
  };

  const commonProps = {
    value: value ?? "",
    onChange,
    placeholder,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    className: `text-base ${dense ? "sm:text-[14px]" : "sm:text-[15px]"}`,
    style: inputStyle,
    required,
    disabled,
    autoComplete,
    ...rest,
  };

  return (
    <div style={{ marginBottom: dense ? 6 : 12 }}>
      {label && <label style={labelStyle}>{label}</label>}
      {as === "textarea" ? (
        <textarea {...commonProps} />
      ) : as === "select" ? (
        <select
          {...commonProps}
          style={{
            ...inputStyle,
            appearance: "none",
            cursor: disabled ? "default" : "pointer",
            color: hasValue ? C.textDark : C.textLight,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000000' stroke-opacity='0.6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 4px center",
            paddingRight: 22,
          }}
        >
          <option value="" disabled>
            {placeholder || "Select..."}
          </option>
          {options?.map((o) =>
            typeof o === "string" ? (
              <option key={o} value={o}>
                {o}
              </option>
            ) : (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ),
          )}
        </select>
      ) : (
        <input type={type} {...commonProps} />
      )}
      {hint && (
        <div
          style={{ fontSize: 11, color: C.textMed, marginTop: dense ? 2 : 3 }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
